import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { provideDayjsDateAdapter } from '@lmc-app/shared/adapters/dayjs-date-adapter';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { RepairProgressDialogComponent } from './repair-progress-dialog.component';
import { RepairResultDialogComponent } from './repair-result-dialog.component';
import {
  FixReport,
  PaymentErrorFinding,
  StatusFixReport,
  StatusFixRow,
  PaymentErrorIssue,
  PaymentErrorScanSummary,
  PaymentErrorsService,
} from '@lmc-app/core/services/payment-errors.service';

/** One "ours vs theirs" pair a finding can be rendered as. */
interface ComparedField {
  label: string;
  ours: keyof PaymentErrorFinding;
  theirs: keyof PaymentErrorFinding;
  money?: boolean;
}

/**
 * One table row: a payment, with every finding the audit raised against it folded in.
 *
 * The audit reports one finding per issue, so a row holding the wrong transaction id whose
 * status is also wrong comes back as two findings carrying identical payment facts. One
 * table row each, they render identically — the cell shows the payment and the fields that
 * differ, and both of those are the same on both findings — so the table appeared to print
 * the same row twice. They are one row instead, which changes nothing about what is shown.
 */
interface MergedFinding extends PaymentErrorFinding {
  /** Every issue folded into this row. What the issue filter matches on. */
  issues: PaymentErrorIssue[];
  /** The findings behind it, so the CSV still writes one line per issue. */
  sources: PaymentErrorFinding[];
}

/**
 * Reconciliation exceptions between our KapchargeTransaction table and KapCharge's own
 * record of the same transactions.
 *
 * Nothing is stored and nothing loads on open: the operator picks a due date, runs the scan,
 * and the findings come back in that one response. Filtering and paging happen here, over
 * the findings already in hand — asking the server for a page would re-run the sweep.
 *
 * One day rather than a range: the sweep reads day by day per wallet, so its cost — and the
 * operator's wait — scales with the number of days asked for. A single day returns in
 * seconds. Both sides of the comparison sit on that one axis: a transaction's dueDate is the
 * day it is submitted, which is the day KapCharge stamps as its createdDateTime.
 *
 * Loan number, due date and amount are treated as the reference — they come from the upload
 * or the operator. The transaction id and the status are the two fields written from
 * KapCharge, so those are what is checked. Matching therefore keys on the business facts and
 * never on the id, which is what lets a WRONG_TRANSACTION_ID finding state the id the row
 * should hold rather than merely that the one it holds belongs to someone else.
 */
@Component({
  selector: 'app-payment-errors',
  templateUrl: './payment-errors.component.html',
  styleUrls: ['./payment-errors.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTableModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
  ],
  // The date the operator types and the date every other surface here prints are the same
  // day, so they are written the same way: the scanned date, a finding's due date and the
  // repair report are all YYYY-MM-DD, and the native adapter's locale format (9/3/2026) was
  // the only place that disagreed. Same adapter and shared format the rest of the app uses.
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [provideDayjsDateAdapter()],
})
export class PaymentErrorsComponent implements OnInit, OnDestroy {
  readonly displayedColumns = ['payment', 'errors'];

  /**
   * The field pairs a finding is rendered as: what we hold, beside what KapCharge holds.
   *
   * Every comparison the audit makes has both sides stored on the finding, so the table can
   * show the actual difference rather than a sentence describing it — an operator fixing a
   * row needs the two values, not prose about them. Order is the order they are worth
   * reading: the id first, because it is the field that gets repaired.
   */
  readonly comparedFields: ComparedField[] = [
    { label: 'Transaction ID', ours: 'kapchargeTransactionId', theirs: 'expectedKapchargeTransactionId' },
    { label: 'Status', ours: 'localStatus', theirs: 'remoteStatus' },
    { label: 'Amount', ours: 'amountInCents', theirs: 'remoteAmountInCents', money: true },
    { label: 'Loan', ours: 'loanNumber', theirs: 'remoteLoanNumber' },
    { label: 'Client reference', ours: 'expectedClientReference', theirs: 'remoteClientReference' },
    { label: 'Wallet', ours: 'expectedMerchantId', theirs: 'remoteMerchantId' },
  ];

  readonly pageSizeOptions = [25, 50, 100];

  /**
   * Every issue the audit can raise, in the order an operator should work through them: the
   * ones that mean money is attached to the wrong record first, then data-quality problems,
   * then the statements the scan makes about itself.
   *
   * The catalogue, not the menu — {@link issueOptions} is narrowed to what a scan found.
   */
  private readonly allIssueOptions: { value: string; label: string }[] = [
    { value: 'WRONG_TRANSACTION_ID', label: 'Wrong transaction id' },
    { value: 'MISSING_TRANSACTION_ID', label: 'Missing transaction id' },
    { value: 'DUPLICATE_ID', label: 'Duplicate transaction id' },
    { value: 'ORPHAN_REMOTE', label: 'Untracked at KapCharge' },
    { value: 'NSF_NOT_IN_NLS', label: 'NSF not in NLS' },
    { value: 'STATUS_MISMATCH', label: 'Status mismatch' },
    { value: 'NOT_FOUND_AT_KAPCHARGE', label: 'Not found at KapCharge' },
    { value: 'AMBIGUOUS_MATCH', label: 'Ambiguous match' },
    { value: 'WRONG_MERCHANT', label: 'Wrong wallet' },
    { value: 'NLS_PAYMENT_NOT_FOUND', label: 'NLS payment not found' },
  ];

  /**
   * What the filter actually offers: 'All issues', plus only the issues the last scan found.
   *
   * Listing the full catalogue meant most options led to an empty table — a scan typically
   * raises two or three kinds, so the other six were dead ends the operator had to try to
   * discover. Rebuilt per scan rather than derived in a getter, so the template is not
   * rebuilding the list on every change-detection pass.
   */
  issueOptions: { value: string; label: string }[] = [{ value: 'all', label: 'All issues' }];

  /** Everything the last scan returned. Filtering and paging run over this, in memory. */
  private allFindings: PaymentErrorFinding[] = [];

  /** Everything the filters currently select, across every page — what Export writes. */
  private filteredFindings: PaymentErrorFinding[] = [];

  /** The page of merged rows the table is showing. */
  findings: MergedFinding[] = [];
  rowCount = 0;

  issueCounts: Partial<Record<PaymentErrorIssue, number>> = {};

  /** Placeholder rows shown while a scan is running. */
  readonly skeletonRows = [1, 2, 3, 4, 5, 6];
  summary: PaymentErrorScanSummary | null = null;
  scannedDate: string | null = null;

  pageIndex = 0;
  pageSize = 50;
  isScanning = false;
  isFixing = false;

  /** What the last fix did. Kept on screen so the result is not just a vanished snackbar. */
  fixReport: FixReport | null = null;

  /** What the last repair's status step did. */
  statusReport: StatusFixReport | null = null;

  /**
   * Names the scan the repair works from. The server kept that scan and writes what it read,
   * so this page sends row ids and nothing else — it cannot say what a repair should write.
   * Null before the first scan, and stale 30 minutes after one (the repair answers 410).
   */
  private scanId: string | null = null;

  loanSearch = new FormControl<string>('', { nonNullable: true });
  issueFilter = new FormControl<string>('all', { nonNullable: true });

  /**
   * The due date to scan. Empty until the operator picks one — a scan is a deliberate act
   * against a specific day, so pre-filling today invited running one without choosing.
   * runScan() refuses a null and says so.
   *
   * The datepicker writes the adapter's own date type, so the value is a dayjs object rather
   * than the Date this is declared as. toIsoDay() is what reads it, and dayjs accepts either.
   */
  scanDate = new FormControl<Date | null>(null);

  /**
   * The window a scan may ask about.
   *
   * Today at the latest, so a day can be checked while it is still being worked. A scan of
   * today is read with that in mind: a due date is the day the transaction is submitted, so
   * rows that have not gone out yet look like discrepancies until the day is through. 180
   * days at the earliest, because the sweep is rate-paced against a budget shared with the
   * submit cron and a mistyped year should not be able to spend it.
   *
   * Fixed at construction rather than recomputed: a getter handing the datepicker a new Date
   * on every change-detection pass churns its comparisons for no gain, and the page is
   * rebuilt on navigation anyway. A tab left open across midnight keeps the previous day's
   * bound until it is reloaded, which costs the operator one day at the edge and nothing else.
   */
  readonly maxScanDate = dayjs().toDate();
  readonly minScanDate = dayjs().subtract(180, 'day').toDate();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly paymentErrorsService: PaymentErrorsService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly dateAdapter: DateAdapter<unknown>
  ) {}

  ngOnInit(): void {
    // No initial load: there is nothing to read until a scan has been run. Both filters work
    // on findings already in memory, so they need no debounce — there is no request to spare.
    this.loanSearch.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.refilter());

    this.issueFilter.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.refilter());

    // They narrow a result, and until a scan has run there is none. Left enabled, the loan
    // field reads as a search that would fetch something, which is the one thing it does not
    // do — the scan deliberately ignores it (see runScan).
    this.setFiltersEnabled(false);

    this.applyDueDateFromUrl();
  }

  /**
   * Opens on a day the URL names, and scans it.
   *
   * The daily alert email links here with `?dueDate=YYYY-MM-DD` — the day it found exceptions
   * on — so the recipient lands on the findings rather than on an empty page they have to
   * re-drive from the date in the subject line. Auto-running is safe because a scan writes
   * nothing; the page's refusal to pre-fill *today* is about not scanning a day nobody chose,
   * and a link that names its day is that choice.
   *
   * A malformed value or a day outside the picker's own window is ignored, and the page opens
   * as it always did — a link that has gone stale should not read as a failed scan.
   */
  private applyDueDateFromUrl(): void {
    const requested = this.route.snapshot.queryParams['dueDate'];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(requested ?? '')) return;

    const day = dayjs(requested);
    if (!day.isValid()) return;
    if (day.isBefore(dayjs(this.minScanDate), 'day') || day.isAfter(dayjs(this.maxScanDate), 'day')) return;

    // Through the picker's own adapter, so the control holds the type the datepicker writes
    // — a dayjs object, as the scanDate note says — and not the Date this is declared as.
    this.scanDate.setValue(this.dateAdapter.deserialize(requested) as Date | null);
    this.runScan();
  }

  /** Both result filters at once; they are only ever meaningful together. */
  private setFiltersEnabled(enabled: boolean): void {
    const options = { emitEvent: false };
    if (enabled) {
      this.loanSearch.enable(options);
      this.issueFilter.enable(options);
    } else {
      this.loanSearch.disable(options);
      this.issueFilter.disable(options);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Runs the scan and keeps the whole day's findings.
   *
   * The loan filter is deliberately not sent: narrowing the audit's local side saves no API
   * calls (the sweep reads every wallet for the window regardless), and sending it would
   * make the filter unable to widen again without another scan.
   */
  runScan(): void {
    const day = this.toIsoDay(this.scanDate.value);
    if (!day) {
      this.snackBar.open('Pick a due date to scan.', 'Close', { duration: 4000 });
      return;
    }

    this.isScanning = true;
    this.paymentErrorsService
      .scan(day)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.allFindings = res.results;
          this.issueCounts = res.issueCounts;
          this.rebuildIssueOptions();
          this.summary = res.summary;
          this.scannedDate = res.scannedDate;
          this.scanId = res.scanId;
          this.fixReport = null;
          this.statusReport = null;
          this.isScanning = false;
          this.setFiltersEnabled(true);
          this.refilter();
        },
        error: (err) => {
          this.isScanning = false;
          this.snackBar.open(err.error?.message || 'Failed to run the scan', 'Close', { duration: 5000 });
        },
      });
  }

  /**
   * Narrows the filter to the issues this scan raised, and drops a selection that no longer
   * exists — a mat-select holding a value with no matching option renders blank.
   */
  private rebuildIssueOptions(): void {
    const counts = this.issueCounts as Record<string, number | undefined>;
    this.issueOptions = [
      { value: 'all', label: 'All issues' },
      ...this.allIssueOptions.filter((option) => (counts[option.value] ?? 0) > 0),
    ];

    if (!this.issueOptions.some((option) => option.value === this.issueFilter.value)) {
      // applyFilters runs immediately after, so this must not trigger a second pass.
      this.issueFilter.setValue('all', { emitEvent: false });
    }
  }

  /** Re-applies both filters from the first page. */
  private refilter(): void {
    this.pageIndex = 0;
    this.applyFilters();
  }

  /**
   * Merges {@link allFindings} into one row per payment, narrows by the two filters, and
   * slices out the current page.
   *
   * The issue filter now matches a row that carries the issue among several: a payment kept
   * for its status mismatch is the same row that holds the wrong id, and there is no longer
   * a second row to show one without the other. The count beside each option is unchanged —
   * it counts findings, which is how many of that issue the scan raised.
   */
  private applyFilters(): void {
    const term = this.loanSearch.value.trim().toLowerCase();
    const issue = this.issueFilter.value;

    const matchesTerm = (f: PaymentErrorFinding) =>
      // Either side of the comparison: searching a loan number must also surface the row
      // that charged it by mistake, which carries that number only in remoteLoanNumber.
      !term || [f.loanNumber, f.remoteLoanNumber]
        .some((value) => (value ?? '').toLowerCase().includes(term));

    const rows = this.mergeByPayment(this.allFindings);
    const matched = rows.filter((row) => {
      if (issue !== 'all' && row.issues.indexOf(issue as PaymentErrorIssue) === -1) return false;
      return row.sources.some(matchesTerm);
    });

    // Export still writes one line per finding: a spreadsheet is filtered and pivoted, and a
    // row carrying several issues at once could not be.
    this.filteredFindings = matched.reduce<PaymentErrorFinding[]>(
      (all, row) => all.concat(row.sources), []);
    this.rowCount = matched.length;
    const start = this.pageIndex * this.pageSize;
    this.findings = matched.slice(start, start + this.pageSize);
  }

  /**
   * Folds the findings of one payment into a single row, first-seen order.
   *
   * Every field the table renders comes from the payment, so the findings of one payment
   * render identically and the extra rows were pure repetition. What is genuinely per-issue
   * is the detail line, and that is kept: the merged row carries all of them.
   *
   * The audit sorts by severity, so a payment lands at the position of its worst issue and
   * the order an operator reads is unchanged. ORPHAN_REMOTE has no row of ours to key on; it
   * is keyed by the KapCharge id instead, and each of those is its own payment anyway.
   */
  private mergeByPayment(findings: PaymentErrorFinding[]): MergedFinding[] {
    const rows = new Map<string, MergedFinding>();

    for (const finding of findings) {
      // != null, so a transactionId the API omitted rather than nulled cannot key every
      // untracked transaction to the same "local:undefined" row.
      const key = finding.transactionId != null
        ? `local:${finding.transactionId}`
        : `remote:${finding.kapchargeTransactionId ?? finding.id}`;

      const row = rows.get(key);
      if (!row) {
        rows.set(key, { ...finding, issues: [finding.issue], sources: [finding] });
        continue;
      }

      row.issues.push(finding.issue);
      row.sources.push(finding);
      // One sentence per issue, and only where the cell shows it at all — a row with fields
      // to compare renders those instead. Identical text is not repeated.
      if (finding.detail && row.detail !== finding.detail) {
        row.detail = row.detail ? `${row.detail} ${finding.detail}` : finding.detail;
      }
    }

    return Array.from(rows.values());
  }

  /**
   * How many rows the fix would write an id onto.
   *
   * Counted over every finding, not the filtered page: the fix applies the whole set, so a
   * filtered view would understate what the button is about to do.
   */
  get fixableCount(): number {
    return this.idFindings.length;
  }

  /** How many rows the fix would write a status onto. */
  get statusFixableCount(): number {
    return this.statusFindings.length;
  }

  /** Transactions KapCharge holds that no row of ours accounts for. */
  get untrackedCount(): number {
    return this.issueCounts['ORPHAN_REMOTE'] ?? 0;
  }

  /** The findings a transaction id can be written from. */
  private get idFindings(): PaymentErrorFinding[] {
    return this.allFindings.filter((f) =>
      f.transactionId !== null
      && !!f.expectedKapchargeTransactionId
      && (f.issue === 'WRONG_TRANSACTION_ID' || f.issue === 'MISSING_TRANSACTION_ID'));
  }

  /** The findings a status can be written from. */
  private get statusFindings(): PaymentErrorFinding[] {
    return this.allFindings.filter((f) =>
      f.transactionId !== null && f.issue === 'STATUS_MISMATCH');
  }

  /** The findings an NSF can be posted to NLS from. */
  private get nsfFindings(): PaymentErrorFinding[] {
    return this.allFindings.filter((f) =>
      f.transactionId !== null && f.issue === 'NSF_NOT_IN_NLS');
  }

  /**
   * The rows to repair — the whole request, and deliberately nothing more.
   *
   * What each row should be written is the server's business: it still holds the scan and
   * works from that. Sending an id here can only ever *narrow* what the scan proposed, so the
   * worst a wrong list can do is repair less than intended, never something else.
   */
  private repairableRowIds(): number[] {
    return [...new Set(
      [...this.idFindings, ...this.statusFindings, ...this.nsfFindings].map((f) => f.transactionId as number),
    )];
  }

  /**
   * Confirms, then runs the whole repair: ids, then statuses, then NSF into NLS.
   *
   * One button rather than two because the order is not optional — correcting a status
   * before the id it depends on would mirror the wrong transaction's result onto the row,
   * which is how a wrong NSF reaches a loan in the first place. The server enforces the
   * order; the page should not offer a way to get it wrong.
   */
  confirmRepair(): void {
    if (this.isFixing || !this.scannedDate || !this.summary) return;
    if (!this.scanId) {
      this.snackBar.open('This scan can no longer be repaired from. Run it again.', 'Close', { duration: 6000 });
      return;
    }
    const ids = this.fixableCount;
    const statuses = this.statusFixableCount;

    this.dialog
      .open(LmcDialogComponent, {
        data: {
          title: 'Fix payment errors',
          content: `This writes ${ids} transaction id(s) and ${statuses} status(es) for ${this.scannedDate}, from what the scan read at KapCharge — posting an NSF into NLS where one applies. Rows already Returned are corrected too, and their NSF in NLS must then be reversed by hand; you will get that list at the end. It cannot be undone.`,
          positiveButtonText: 'Run repair',
          negativeButtonText: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed === true) this.runRepair();
      });
  }

  private runRepair(): void {
    if (!this.scannedDate || !this.scanId) return;

    const rowIds = this.repairableRowIds();

    // Modal for the duration: the run writes ids, then statuses, then NSF into NLS, and none
    // of that should be racing an operator starting a second scan or navigating away.
    const progress = this.dialog.open(RepairProgressDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: true,
      data: { scannedDate: this.scannedDate, idCount: this.fixableCount },
    });

    this.isFixing = true;
    this.paymentErrorsService
      .repair(this.scanId, rowIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isFixing = false;
          progress.close();
          this.fixReport = res.idReport;
          this.statusReport = res.statusReport;
          this.applyFixReport(res.idReport, res.statusReport);
          this.showRepairResult(res.idReport, res.statusReport);
        },
        error: (err) => {
          this.isFixing = false;
          progress.close();
          // 410: the server no longer holds this scan, so nothing on screen can be repaired
          // from. Drop the handle so the button cannot re-post a scan id that is gone.
          if (err.status === 410) this.scanId = null;
          this.snackBar.open(err.error?.message || 'The repair failed', 'Close', { duration: 8000 });
        },
      });
  }

  /**
   * Puts the outcome in front of the operator, NSF reversals included.
   *
   * A dialog, not a snackbar: an NSF already in NLS cannot be reversed from here, so each
   * one is outstanding work that has to be acknowledged and taken away as a list.
   */
  private showRepairResult(idReport: FixReport, statusReport: StatusFixReport): void {
    this.dialog.open(RepairResultDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { scannedDate: this.scannedDate as string, idReport, statusReport },
    });
  }

  /**
   * Brings the findings in hand into line with what the repair actually did, patched in
   * place rather than re-scanned.
   */
  private applyFixReport(report: FixReport, statusReport: StatusFixReport): void {
    const repaired = new Set(report.fixed.map((f) => f.transactionId));
    this.allFindings = this.allFindings.filter((f) =>
      !(f.transactionId !== null && repaired.has(f.transactionId)));

    // A status actually written settles its own finding. Only 'updated' — a row left alone by
    // the out-of-order guard, or one whose write failed, is still an exception.
    const statusWritten = new Set(
      statusReport.results.filter((r) => r.outcome === 'updated').map((r) => r.id));
    this.allFindings = this.allFindings.filter((f) =>
      !((f.issue === 'STATUS_MISMATCH' || f.issue === 'NSF_NOT_IN_NLS')
        && f.transactionId !== null && statusWritten.has(f.transactionId)));

    for (const c of report.cleared) {
      const row = this.allFindings.find((f) => f.transactionId === c.transactionId);
      if (row) row.kapchargeTransactionId = null;
    }

    this.issueCounts = this.allFindings.reduce((counts, f) => {
      counts[f.issue] = (counts[f.issue] ?? 0) + 1;
      return counts;
    }, {} as Partial<Record<PaymentErrorIssue, number>>);
    if (this.summary) this.summary = { ...this.summary, findingCount: this.allFindings.length };

    this.rebuildIssueOptions();
    this.refilter();
  }

  /**
   * The status rows worth listing: the ones the run did not apply.
   *
   * `unchanged` is the bulk of any sweep and `updated` is the run doing its job — neither is
   * something an operator acts on. What is left is what was refused or failed.
   */
  statusRowsWorthShowing(report: StatusFixReport): StatusFixRow[] {
    return report.results.filter((r) => r.outcome !== 'unchanged' && r.outcome !== 'updated');
  }

  get canExport(): boolean {
    return this.filteredFindings.length > 0;
  }

  /**
   * Writes the filtered findings — every page of them, not just the one on screen — as CSV.
   *
   * Built in the browser from findings already in hand: the scan is not stored, so asking
   * the server for the same list would mean running the whole sweep a second time.
   */
  exportCsv(): void {
    if (!this.canExport) return;

    const columns: { header: string; value: (f: PaymentErrorFinding) => unknown }[] = [
      { header: 'Issue', value: (f) => f.issue },
      { header: 'Severity', value: (f) => f.severity },
      { header: 'Loan number', value: (f) => f.loanNumber },
      { header: 'Customer name', value: (f) => f.customerName },
      // Plain decimals, not the "$12.34" the table shows, so a spreadsheet can sum them.
      { header: 'Amount', value: (f) => this.centsToDecimal(f.amountInCents) },
      { header: 'Due date', value: (f) => f.dueDate },
      { header: 'Our status', value: (f) => f.localStatus },
      { header: 'Submit error', value: (f) => f.localError },
      { header: 'Our transaction id', value: (f) => f.kapchargeTransactionId },
      { header: 'Expected transaction id', value: (f) => f.expectedKapchargeTransactionId },
      { header: 'KapCharge loan number', value: (f) => f.remoteLoanNumber },
      { header: 'KapCharge amount', value: (f) => this.centsToDecimal(f.remoteAmountInCents) },
      { header: 'KapCharge status', value: (f) => f.remoteStatus },
      { header: 'KapCharge created', value: (f) => f.remoteCreatedDateTime },
      { header: 'KapCharge wallet', value: (f) => f.remoteMerchantId },
      { header: 'Expected wallet', value: (f) => f.expectedMerchantId },
      { header: 'Detail', value: (f) => f.detail },
    ];

    const lines = [
      columns.map((c) => this.csvCell(c.header)).join(','),
      ...this.filteredFindings.map((f) => columns.map((c) => this.csvCell(c.value(f))).join(',')),
    ];

    // BOM so Excel reads it as UTF-8 — without it, an accented customer name arrives mangled.
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-errors-${this.scannedDate ?? 'scan'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private centsToDecimal(cents: number | null): string {
    if (cents === null || cents === undefined) return '';
    return (cents / 100).toFixed(2);
  }

  /**
   * One CSV cell: quoted, with embedded quotes doubled.
   *
   * A leading =, +, - or @ is prefixed with an apostrophe. Excel treats such a value as a
   * formula, so a customer name or detail string starting with one would be executed rather
   * than displayed — the apostrophe makes it text.
   */
  private csvCell(value: unknown): string {
    if (value === null || value === undefined) return '""';
    const text = String(value);
    const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  }

  private toIsoDay(date: Date | null): string | null {
    if (!date) return null;
    // dayjs formats in local time. Date.toISOString() would convert to UTC and hand the
    // server the previous day for anyone west of Greenwich, which is everyone here.
    return dayjs(date).format('YYYY-MM-DD');
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyFilters();
  }

  trackById(_index: number, finding: PaymentErrorFinding): number {
    return finding.id;
  }

  /**
   * A scan that could not read everything it meant to produces findings indistinguishable
   * from real ones — an unread page looks exactly like a missing transaction — so the page
   * has to say so rather than presenting the list as complete.
   */
  get scanIncomplete(): boolean {
    return !!this.summary?.truncatedDetail;
  }

  get emptyStateMessage(): string {
    if (!this.summary) return 'No scan has been run yet. Pick a due date and run one.';
    if (this.loanSearch.value.trim() || this.issueFilter.value !== 'all') {
      return 'No findings match this filter.';
    }
    return 'No discrepancies found for this date. Every payment matches KapCharge.';
  }

  /**
   * The fields that actually differ on this finding, as {label, ours, theirs} rows.
   *
   * A pair is shown when the two sides disagree and KapCharge has a value — a field we hold
   * and KapCharge does not is an absence, not a disagreement, and listing it would fill the
   * column with noise on findings that have no counterpart at all. Those two cases
   * (`isRemoteOnly`, `hasNoMatch`) are rendered separately.
   */
  differences(row: PaymentErrorFinding): { label: string; ours: string; theirs: string }[] {
    return this.comparedFields
      .filter((f) => {
        const theirs = row[f.theirs];
        if (theirs === null || theirs === undefined) return false;
        return String(row[f.ours] ?? '') !== String(theirs);
      })
      .map((f) => ({
        label: f.label,
        ours: this.displayValue(row[f.ours], f.money),
        theirs: this.displayValue(row[f.theirs], f.money),
      }));
  }

  /** No row on our side: KapCharge holds a transaction nothing of ours accounts for. */
  isRemoteOnly(row: PaymentErrorFinding): boolean {
    return row.transactionId === null;
  }

  /** No transaction on KapCharge's side matched this row, so there is nothing to diff. */
  hasNoMatch(row: PaymentErrorFinding): boolean {
    return !this.isRemoteOnly(row) && !row.remoteClientReference && !row.expectedKapchargeTransactionId;
  }

  private displayValue(value: unknown, money?: boolean): string {
    if (value === null || value === undefined || value === '') return 'none';
    if (money) return this.formatCents(Number(value));
    return String(value);
  }

  /**
   * A scan duration as an operator reads it: seconds to one decimal under a minute, then
   * minutes and whole seconds. Milliseconds are noise at this scale.
   */
  formatDuration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) return '—';
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${String(Math.round(seconds % 60)).padStart(2, '0')}s`;
  }

  formatCents(cents: number | null): string {
    if (cents === null || cents === undefined) return '—';
    return `$${(cents / 100).toFixed(2)}`;
  }
}
